import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { postWeeklyReportOpen } from '@pray/shared/services/studioService/superfunnelWeeklyReport';

import useAppRoutes from 'hooks/useAppRoutes';

// The longest the click recording may hold the reader up.
const RECORD_TIMEOUT_MS = 3000;

// The email's leader is the ministry's public artist id. Anything else goes home rather than into a URL.
const LEADER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Public page the SuperFunnel weekly report's "Open your Revenue Overview" button opens. It records the
 * click, then hands over to the ministry's Revenue Overview, which asks a signed-out reader to log in.
 * No data-viewname: Amplitude logs the page's URL, and this one carries the token.
 */
export default function WeeklyReportOpenPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const leader = searchParams.get('leader') ?? '';
  const appRoutes = useAppRoutes();
  const destination = LEADER_ID_PATTERN.test(leader) ? appRoutes.artistRevenueOverview(leader) : appRoutes.index();
  const pagePath = appRoutes.weeklyReportOpen();
  const started = useRef(false);

  // No cleanup: StrictMode runs this twice, and cancelling the first run would leave the reader here.
  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Drop the token from this page's URL first: the Overview would otherwise get it as
    // document.referrer, which Amplitude records (includeReferrer).
    window.history.replaceState(window.history.state, '', pagePath);

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, RECORD_TIMEOUT_MS);
    });
    // The reader goes on whatever the server says.
    const recording = token ? postWeeklyReportOpen(token).catch(() => undefined) : Promise.resolve();

    Promise.race([recording, timeout]).then(() => {
      clearTimeout(timer);
      // A full page load, not navigate(): StudioContext reads the leader from the URL only when it mounts.
      window.location.replace(destination);
    });
  }, [token, destination, pagePath]);

  return (
    <main className="flex min-h-screen items-start justify-center bg-secondary px-4 py-16 sm:py-24">
      <div className="w-full max-w-md rounded-xl border bg-background p-8">
        <p className="text-sm font-bold text-foreground">PRAY Studio</p>
        <h1 className="mt-8 text-balance text-2xl font-bold text-foreground">Opening your Revenue Overview…</h1>
      </div>
    </main>
  );
}
